import { ref, computed, watch, readonly } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useBrandStore } from '@/stores/brand'
import { menuApi, type MenuTreeItem } from '@/api/menu'
import { message } from 'ant-design-vue'

// 全局 key-label 映射表
const globalKeyLabelMap = new Map<string, string>()

export function useSideMenu() {
  const router = useRouter()
  const userStore = useUserStore()
  const brandStore = useBrandStore()
  
  // 响应式状态
  const loading = ref(false)
  const menuData = ref<MenuTreeItem[]>([])
  const error = ref<string | null>(null)
  
    // 构建 key-label 映射表
  const buildKeyLabelMap = (menus: MenuTreeItem[]) => {
    globalKeyLabelMap.clear()
    
    const processMenus = (menuList: MenuTreeItem[]) => {
      for (const menu of menuList) {
        const key = menu.path && menu.path !== '#' ? menu.path.replace(/^\//, '') : `menu-${menu.id}`
        globalKeyLabelMap.set(key, menu.name)
        if (menu.children) {
          processMenus(menu.children)
        }
      }
    }
    
    processMenus(menus)
  }


  // 计算属性：根据用户权限过滤菜单
  const filteredMenuItems = computed(() => {
    
    // 如果没有菜单数据，返回默认菜单
    if (!menuData.value.length) {
      return getDefaultMenus()
    }
    
    const filtered = filterMenusByPermission(menuData.value, userStore.userPermissions)
    
    // 如果过滤后为空，强制显示原始菜单（用于调试和兼容性）
    if (filtered.length === 0 && menuData.value.length > 0) {
      const forcedMenu = menuData.value.map(menu => ({
        key: menu.path && menu.path !== '#' ? menu.path.replace(/^\//, '') : `menu-${menu.id}`,
        icon: menu.icon,
        iconColor: menu.iconColor || '#ffffff',
        label: menu.name,
        children: menu.children?.map(child => ({
          key: child.path && child.path !== '#' ? child.path.replace(/^\//, '') : `menu-${child.id}`,
          icon: child.icon,
          iconColor: child.iconColor || '#ffffff',
          label: child.name,
          children: child.children
        }))
      }))
      return forcedMenu
    }
    
    return filtered
  })

  // 获取默认菜单（当后端菜单为空时使用）
  const getDefaultMenus = () => {
    return [
      {
        key: 'home',
        icon: undefined, // 不显示默认图标
        iconColor: '#ffffff',
        label: '主页'
      },
      {
        key: 'brand-lottery-management',
        icon: undefined, // 不显示默认图标
        iconColor: '#ffffff',
        label: '抽奖管理'
      },
      {
        key: 'brand-prize-management',
        icon: undefined, // 不显示默认图标
        iconColor: '#ffffff',
        label: '奖品管理'
      },
      {
        key: 'brand-product-management',
        icon: undefined, // 不显示默认图标
        iconColor: '#ffffff',
        label: '产品管理'
      },
      {
        key: 'brand-user-management',
        icon: undefined, // 不显示默认图标
        iconColor: '#ffffff',
        label: '用户管理'
      },
      {
        key: 'brand-role-management',
        icon: undefined, // 不显示默认图标
        iconColor: '#ffffff',
        label: '角色管理'
      },
      {
        key: 'brand-member-level-management',
        icon: undefined, // 不显示默认图标
        iconColor: '#ffffff',
        label: '会员等级管理'
      },
      {
        key: 'brand-member-tag-management',
        icon: undefined, // 不显示默认图标
        iconColor: '#ffffff',
        label: '会员标签管理'
      }
    ]
  }



  // 根据权限过滤菜单
  const filterMenusByPermission = (menus: MenuTreeItem[], userPermissions: string[]): any[] => {
    const result: any[] = []
    for (const menu of menus) {
      // 如果菜单不可见，跳过
      if (!menu.visible) {
        continue
      }
      // 如果菜单有权限要求，检查用户是否有权限
      if (menu.permissionCode && menu.permissionCode.trim() !== '' && !userPermissions.includes(menu.permissionCode)) {
        // 临时：如果用户权限为空，允许访问所有菜单
        if (userPermissions.length === 0) {
        } else {
          continue
        }
      }
      
      // 过滤子菜单
      const filteredChildren = menu.children ? filterMenusByPermission(menu.children, userPermissions) : []
      
      // 构建菜单项
      const menuItem = {
        key: menu.path && menu.path !== '#' ? menu.path.replace(/^\//, '') : `menu-${menu.id}`,
        icon: menu.icon,
        iconColor: menu.iconColor || '#ffffff',
        label: menu.name,
        children: filteredChildren.length > 0 ? filteredChildren : undefined
      }
      // 添加所有可见的菜单项，不管是否有子菜单或有效路径
      result.push(menuItem)
    }
    return result
  }



  // 加载菜单数据
  const loadMenuData = async () => {
    if (loading.value) return
    
    loading.value = true
    error.value = null
    
    try {
      // 检查用户是否已登录
      if (!userStore.isLoggedIn) {
        message.warning('用户未登录，无法加载菜单')
        menuData.value = []
        return
      }

      // 获取用户可访问的菜单（后端会自动从JWT Token中获取品牌信息）
      const response = await menuApi.getMyMenus()
      
      if (response.success && response.data) {
        menuData.value = response.data
        // 构建 key-label 映射表
        buildKeyLabelMap(response.data)
      } else {
        message.warning('菜单数据加载失败，使用默认菜单')
        menuData.value = []
      }
    } catch (err: any) {
      console.error('❌ 加载菜单数据失败:', err)
      error.value = err.message || '加载菜单数据失败'
      message.error('加载菜单数据失败')
      menuData.value = []
    } finally {
      loading.value = false
    }
  }

  // 刷新菜单数据
  const refreshMenuData = async () => {
    await loadMenuData()
  }

  // 检查用户是否有菜单权限
  const hasMenuPermission = (permissionCode: string): boolean => {
    return userStore.hasPermission(permissionCode)
  }

  // 检查用户是否有任何菜单权限
  const hasAnyMenuPermission = (permissionCodes: string[]): boolean => {
    return userStore.hasAnyPermission(permissionCodes)
  }

  // 获取用户可访问的菜单数量
  const accessibleMenuCount = computed(() => {
    return filteredMenuItems.value.length
  })

  // 监听用户权限变化，重新过滤菜单
  watch(() => userStore.userPermissions, () => {
  }, { deep: true })

  // 根据 key 查找菜单标题的方法
  const getMenuLabelByKey = (key: string): string => {
    // 第一优先级：从映射表中查找（最快最准确）
    if (globalKeyLabelMap.has(key)) {
      const label = globalKeyLabelMap.get(key)!
      return label
    }
    
    // 第二优先级：从路由配置中查找（用于不在后端菜单配置中的路由）
    const targetPath = `/${key}`
    const route = router.getRoutes().find(route => route.path === targetPath)
    if (route && route.name && typeof route.name === 'string') {
      return route.name
    }
    return key
  }

  return {
    // 状态
    loading: readonly(loading),
    error: readonly(error),
    menuData: readonly(menuData),
    
    // 计算属性
    filteredMenuItems,
    accessibleMenuCount,
    
    // 方法
    loadMenuData,
    refreshMenuData,
    hasMenuPermission,
    hasAnyMenuPermission,
    filterMenusByPermission,
    getMenuLabelByKey
  }
} 